import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Save, Loader2, Briefcase, MapPin, Clock, ToggleLeft, ToggleRight } from 'lucide-react';
import './AdminCareers.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCareers = () => {
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCareer, setEditingCareer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    job_title: '',
    description: '',
    requirements: '',
    location: 'Gandhinagar, Gujarat',
    experience: '',
    job_type: 'Full-time',
    status: 'open'
  });

  useEffect(() => {
    fetchCareers();
  }, []);

  const fetchCareers = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/careers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCareers(data);
      }
    } catch (error) {
      console.error('Failed to fetch careers:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingCareer(null);
    setFormData({
      job_title: '',
      description: '',
      requirements: '',
      location: 'Gandhinagar, Gujarat',
      experience: '',
      job_type: 'Full-time',
      status: 'open'
    });
    setShowModal(true);
  };

  const openEditModal = (career) => {
    setEditingCareer(career);
    setFormData({
      job_title: career.job_title,
      description: career.description,
      requirements: career.requirements || '',
      location: career.location,
      experience: career.experience,
      job_type: career.job_type,
      status: career.status
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('adminToken');
      const url = editingCareer 
        ? `${API_URL}/api/admin/careers/${editingCareer.id}`
        : `${API_URL}/api/admin/careers`;
      
      const response = await fetch(url, {
        method: editingCareer ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        fetchCareers();
        setShowModal(false);
      }
    } catch (error) {
      console.error('Failed to save career:', error);
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (career) => {
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${API_URL}/api/admin/careers/${career.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: career.status === 'open' ? 'closed' : 'open' })
      });
      fetchCareers();
    } catch (error) {
      console.error('Failed to toggle status:', error);
    }
  };

  const deleteCareer = async (careerId) => {
    if (!window.confirm('Are you sure you want to delete this job posting?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/careers/${careerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchCareers();
      }
    } catch (error) {
      console.error('Failed to delete career:', error);
    }
  };

  return (
    <div className="admin-careers" data-testid="admin-careers">
      <div className="page-header">
        <div>
          <h1>Careers</h1>
          <p>Manage job postings</p>
        </div>
        <button className="add-btn" onClick={openCreateModal} data-testid="add-career-btn">
          <Plus size={18} />
          <span>Add Job</span>
        </button>
      </div>

      {loading ? (
        <div className="loading-container">
          <Loader2 className="spin" size={32} />
          <span>Loading jobs...</span>
        </div>
      ) : careers.length === 0 ? (
        <div className="empty-container">
          <Briefcase size={48} />
          <h3>No job postings yet</h3>
          <p>Create your first job posting to start hiring.</p>
          <button className="add-btn" onClick={openCreateModal}>
            <Plus size={18} />
            <span>Add Job</span>
          </button>
        </div>
      ) : (
        <div className="careers-list">
          {careers.map((career) => (
            <div key={career.id} className={`career-card ${career.status}`} data-testid={`career-card-${career.id}`}>
              <div className="career-header">
                <span className={`status-badge ${career.status}`}>
                  {career.status === 'open' ? 'Open' : 'Closed'}
                </span>
                <button className="toggle-btn" onClick={() => toggleStatus(career)}>
                  {career.status === 'open' ? <ToggleRight size={24} className="on" /> : <ToggleLeft size={24} className="off" />}
                </button>
              </div>
              <h3 className="career-title">{career.job_title}</h3>
              <div className="career-meta">
                <span><MapPin size={14} /> {career.location}</span>
                <span><Briefcase size={14} /> {career.job_type}</span>
                <span><Clock size={14} /> {career.experience}</span>
              </div>
              <p className="career-desc">{career.description?.substring(0, 120)}...</p>
              <div className="career-actions">
                <button className="action-btn" onClick={() => openEditModal(career)}>
                  <Edit2 size={16} /> Edit
                </button>
                <button className="action-btn delete" onClick={() => deleteCareer(career.id)}>
                  <Trash2 size={16} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Career Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCareer ? 'Edit Job Posting' : 'New Job Posting'}</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="career-form">
              <div className="form-group">
                <label>Job Title *</label>
                <input
                  type="text"
                  value={formData.job_title}
                  onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                  placeholder="e.g., Senior Video Editor"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Job Type</label>
                  <select
                    value={formData.job_type}
                    onChange={(e) => setFormData({ ...formData, job_type: e.target.value })}
                  >
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Freelance">Freelance</option>
                    <option value="Internship">Internship</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Experience Required *</label>
                <input
                  type="text"
                  value={formData.experience}
                  onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                  placeholder="e.g., 2-3 years"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the role and responsibilities..."
                  rows={5}
                  required
                />
              </div>

              <div className="form-group">
                <label>Requirements</label>
                <textarea
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                  placeholder="List the skills and qualifications required..."
                  rows={4}
                />
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={saving}>
                  {saving ? (
                    <><Loader2 size={16} className="spin" /><span>Saving...</span></>
                  ) : (
                    <><Save size={16} /><span>{editingCareer ? 'Update' : 'Create'} Job</span></>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCareers;
